import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, TrendingUp, Home, Calendar, ArrowRight, CheckCircle, Building2, FileCheck, Users, Clock, Banknote, Scale, Send, Lock, Award, Briefcase, User, BookOpen, ExternalLink, Plug, BarChart3, Linkedin, Share2, Calculator, PoundSterling, AlertTriangle, Download } from "lucide-react";
import "@/styles/govtech.css";

const TenuresLogo = () => (
  <span className="text-xl font-bold text-[#333333]">
    Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
  </span>
);

const SolutionCard = ({ icon: Icon, title, subtitle, description, status, onClick }) => (
  <div 
    onClick={onClick}
    className="bg-white border border-gray-200 p-8 hover:border-[#00703C] hover:shadow-lg transition-all cursor-pointer group"
  >
    <div className="w-14 h-14 rounded-lg bg-[#00703C] bg-opacity-10 flex items-center justify-center mb-6 group-hover:bg-opacity-20 transition-all">
      <Icon className="text-[#00703C]" size={28} strokeWidth={1.5} />
    </div>
    <div className="flex items-center gap-2 mb-2">
      <h3 className="text-xl font-bold text-[#333333]">{title}</h3>
      {status === "live" && (
        <span className="text-xs bg-[#00703C] text-white px-2 py-0.5 rounded">LIVE</span>
      )}
      {status === "coming" && (
        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">COMING SOON</span>
      )}
    </div>
    <p className="text-sm font-medium text-[#00703C] mb-3">{subtitle}</p>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    <div className="mt-6 flex items-center text-[#00703C] font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity">
      {status === "live" ? "Launch Module" : "Learn More"} <ArrowRight size={16} className="ml-2" />
    </div>
  </div>
);

const CaseStudyCard = ({ challenge, solution, outcome, stats }) => (
  <div className="bg-white border border-gray-200 p-8">
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-xs font-semibold text-red-600 uppercase tracking-wide">The Challenge</span>
        </div>
        <p className="text-gray-700">{challenge}</p>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-2 rounded-full bg-[#00703C]" />
          <span className="text-xs font-semibold text-[#00703C] uppercase tracking-wide">The TenURes Solution</span>
        </div>
        <p className="text-gray-700">{solution}</p>
      </div>
      <div className="bg-[#00703C] bg-opacity-5 border-l-4 border-[#00703C] p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="text-[#00703C]" size={16} />
          <span className="text-xs font-semibold text-[#00703C] uppercase tracking-wide">The Outcome</span>
        </div>
        <p className="text-[#333333] font-medium">{outcome}</p>
        {stats && (
          <div className="flex gap-6 mt-4">
            {stats.map((stat, i) => (
              <div key={i}>
                <p className="text-2xl font-bold text-[#00703C]">{stat.value}</p>
                <p className="text-xs text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

const InsightCard = ({ title, category, readTime }) => (
  <div className="bg-white border border-gray-200 p-5 hover:border-[#00703C] transition-all cursor-pointer group">
    <span className="text-xs font-semibold text-[#00703C] uppercase tracking-wide">{category}</span>
    <h4 className="font-semibold text-[#333333] mt-2 group-hover:text-[#00703C] transition-colors">{title}</h4>
    <p className="text-xs text-gray-500 mt-2">{readTime}</p>
  </div>
);

export default function B2BLanding() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    authority: "",
    role: "",
    message: ""
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // ROI Calculator state
  const [roiData, setRoiData] = useState({
    stockSize: "",
    arrearsRate: "8",
    avgEvictionCost: "12000"
  });
  const [roiCalculated, setRoiCalculated] = useState(false);
  const [roiEmail, setRoiEmail] = useState("");
  const [roiEmailSent, setRoiEmailSent] = useState(false);
  
  // Calculate ROI
  const calculateROI = () => {
    const stock = parseInt(roiData.stockSize) || 0;
    const arrearsRate = parseFloat(roiData.arrearsRate) || 8;
    const evictionCost = parseInt(roiData.avgEvictionCost) || 12000;
    
    // Assumptions based on industry data
    const atRiskResidents = Math.round(stock * (arrearsRate / 100));
    const earlyInterventionRate = 0.35; // 35% use the tool
    const preventionRate = 0.78; // 78% of those avoid eviction
    
    const potentialEvictions = Math.round(atRiskResidents * 0.15); // 15% of arrears cases lead to eviction
    const evictionsPrevented = Math.round(potentialEvictions * earlyInterventionRate * preventionRate);
    const costSaved = evictionsPrevented * evictionCost;
    const residentsHelped = Math.round(atRiskResidents * earlyInterventionRate);
    
    return {
      atRiskResidents,
      evictionsPrevented: Math.max(1, evictionsPrevented),
      costSaved,
      residentsHelped,
      officerHoursSaved: Math.round(residentsHelped * 2.5) // 2.5 hours saved per pre-qualified resident
    };
  };
  
  const roiResults = roiCalculated ? calculateROI() : null;
  
  const handleRoiEmailSubmit = (e) => {
    e.preventDefault();
    const leadData = {
      email: roiEmail,
      stockSize: roiData.stockSize,
      results: roiResults,
      timestamp: new Date().toISOString()
    };
    console.log("[ROI Calculator Lead]", leadData);
    
    // Store for analytics
    const leads = JSON.parse(localStorage.getItem('tenures_roi_leads') || '[]');
    leads.push(leadData);
    localStorage.setItem('tenures_roi_leads', JSON.stringify(leads));
    
    setRoiEmailSent(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("[Pilot Request]", formData);
    setFormSubmitted(true);
  };

  const solutions = [
    {
      icon: Shield,
      title: "Succession Rights",
      subtitle: "Who Can Stay After a Tenant Passes",
      description: "Help residents understand if they qualify to stay in their home. Automated 12-month evidence checklists reduce invalid claims and officer time.",
      status: "live",
      path: "/succession-triage"
    },
    {
      icon: TrendingUp,
      title: "Right to Buy",
      subtitle: "The Path to Home Ownership",
      description: "Calculate discount entitlements and track mortgage readiness. Forecast capital receipts with anonymised intent-to-buy data.",
      status: "live",
      path: "/rtb-triage"
    },
    {
      icon: Home,
      title: "Downsizing & Exchange",
      subtitle: "Right-Sizing Homes, Freeing Up Stock",
      description: "Match under-occupiers with families who need space. Financial incentive calculators make downsizing attractive and reduce TA spend.",
      status: "live",
      path: "/liveability-triage"
    },
    {
      icon: Calendar,
      title: "Tenancy Milestones",
      subtitle: "Important Dates & Deadlines",
      description: "Automated reminders for starter-to-secure conversions, RTB eligibility, and annual reviews. Residents arrive prepared, officers save time.",
      status: "live",
      path: "/milestone-guardian"
    }
  ];

  const insights = [
    { title: "How to Reduce 'Bed-Blocking' in 3 Steps", category: "Succession", readTime: "5 min read" },
    { title: "The True Cost of an Ineligible Succession Claim", category: "Compliance", readTime: "4 min read" },
    { title: "Why Residents Delay RTB Applications (And How to Help)", category: "Right to Buy", readTime: "6 min read" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <TenuresLogo />
          <div className="flex items-center gap-6">
            <button 
              onClick={() => navigate('/about')}
              className="text-gray-600 hover:text-[#00703C] text-sm font-medium transition-colors"
            >
              About Us
            </button>
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-[#00703C] text-sm font-medium transition-colors"
            >
              Partner Dashboard
            </button>
            <button 
              onClick={() => navigate('/resident')}
              className="text-gray-600 hover:text-[#00703C] text-sm font-medium transition-colors"
            >
              Resident Portal
            </button>
            <button 
              onClick={() => document.getElementById('pilot-form').scrollIntoView({ behavior: 'smooth' })}
              className="govtech-btn govtech-btn-primary text-sm px-5 py-2.5"
            >
              Request Pilot
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-white pt-16 pb-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-[#00703C] bg-opacity-10 text-[#00703C] px-4 py-2 rounded-full mb-8">
            <Building2 size={18} />
            <span className="text-sm font-semibold">For Local Authorities & Housing Associations</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-[#333333]">
            The Digital Triage Layer for<br />
            <span className="text-[#00703C]">High-Performing Housing Teams</span>
          </h1>
          
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Standardise your residency audits, prevent fraud, and optimise housing stock with 10 years of frontline expertise built into every click.
          </p>
          
          {/* Social Share for Housing Professionals */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="text-sm text-gray-500">Share with a colleague:</span>
            <button
              onClick={() => {
                const shareUrl = window.location.origin;
                const shareText = "Check out TenURes - a digital triage platform that helps Housing Authorities reduce admin burden by 30% and prevent succession fraud. Built with 10 years of frontline housing expertise.";
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white text-sm font-medium hover:bg-[#006097] transition-colors"
            >
              <Linkedin size={16} />
              Share on LinkedIn
            </button>
            <button
              onClick={() => {
                const shareUrl = window.location.origin;
                const shareText = "Check out TenURes - a digital triage platform for Housing Authorities. Reduces admin burden by 30%. Built with 10 years of frontline expertise.";
                if (navigator.share) {
                  navigator.share({ title: 'TenURes - Housing Triage Platform', text: shareText, url: shareUrl });
                } else {
                  navigator.clipboard.writeText(shareUrl);
                  alert('Link copied to clipboard!');
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              <Share2 size={16} />
              Copy Link
            </button>
          </div>
        </div>
      </section>

      {/* Dual-Track Navigation - "Choose Your Path" */}
      <section className="pb-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* B2B Path */}
            <div 
              onClick={() => navigate('/dashboard')}
              className="bg-[#333333] text-white p-8 cursor-pointer hover:bg-[#444444] transition-all group"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-white bg-opacity-10 flex items-center justify-center">
                  <Briefcase className="text-white" size={28} />
                </div>
                <div>
                  <p className="text-sm text-gray-400">I am a</p>
                  <p className="text-xl font-bold">Housing Professional</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm mb-4">
                Preview our analytics dashboard and see how TenURes helps authorities track resident engagement.
              </p>
              <div className="flex items-center text-[#00703C] font-medium">
                Preview Demo Dashboard <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>

            {/* Resident Path */}
            <div 
              onClick={() => navigate('/resident')}
              className="bg-[#00703C] text-white p-8 cursor-pointer hover:bg-[#005a30] transition-all group"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                  <User className="text-white" size={28} />
                </div>
                <div>
                  <p className="text-sm text-green-200">I am a</p>
                  <p className="text-xl font-bold">Resident</p>
                </div>
              </div>
              <p className="text-green-100 text-sm mb-4">
                Get free, anonymous guidance on succession rights, Right to Buy, and more. No login required.
              </p>
              <div className="flex items-center text-white font-medium">
                Start My Free Triage <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-[#F8F8F8] border-y border-gray-200 py-4">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Scale size={16} className="text-[#00703C]" />
              <span>Built to Housing Act 1985 Logic</span>
            </div>
            <div className="h-4 w-px bg-gray-300 hidden md:block" />
            <div className="flex items-center gap-2">
              <Lock size={16} className="text-[#00703C]" />
              <span>GDPR Privacy-First Architecture</span>
            </div>
            <div className="h-4 w-px bg-gray-300 hidden md:block" />
            <div className="flex items-center gap-2">
              <Award size={16} className="text-[#00703C]" />
              <span>10-Year Frontline Vetted</span>
            </div>
          </div>
        </div>
      </section>

      {/* Modular Solutions Grid */}
      <section id="solutions" className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-600 px-3 py-1 rounded-full mb-4">
              <Plug size={14} />
              <span className="text-xs font-semibold uppercase tracking-wide">Modular Architecture</span>
            </div>
            <h2 className="text-3xl font-bold text-[#333333] mb-4">Four Integrated Modules. One Platform.</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Each module reduces manual processing by 30-40% and generates clean, exportable data for your existing housing management systems.
            </p>
          </div>

          
          <div className="grid md:grid-cols-2 gap-6">
            {solutions.map((solution) => (
              <SolutionCard
                key={solution.title}
                {...solution}
                onClick={() => solution.status === "live" && navigate(solution.path)}
              />
            ))}
          </div>

          {/* Modular Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="text-center p-6">
              <Plug className="text-[#00703C] mx-auto mb-3" size={32} />
              <h4 className="font-bold text-[#333333] mb-2">Interoperable</h4>
              <p className="text-sm text-gray-600">Clean data exports integrate with your existing HMS, removing manual data entry.</p>
            </div>
            <div className="text-center p-6">
              <BarChart3 className="text-[#00703C] mx-auto mb-3" size={32} />
              <h4 className="font-bold text-[#333333] mb-2">Proactive, Not Reactive</h4>
              <p className="text-sm text-gray-600">Shift from chasing arrears to predicting financial resilience.</p>
            </div>
            <div className="text-center p-6">
              <FileCheck className="text-[#00703C] mx-auto mb-3" size={32} />
              <h4 className="font-bold text-[#333333] mb-2">Single View of Resident</h4>
              <p className="text-sm text-gray-600">Consolidate 10 years of evidence into a single, verified PDF Audit.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories - Civica Framework */}
      <section className="py-20 bg-[#F8F8F8]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-4">Success Stories</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Real outcomes from authorities using the TenURes triage methodology.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <CaseStudyCard
              challenge="A London Borough was spending £4M annually on Temporary Accommodation while 15% of their 3-bed stock was under-occupied."
              solution="Implemented the Under-Occupation Optimizer to provide residents with a 'Stress-Free Move' digital toolkit and automated financial incentive calculators."
              outcome="Significant TA cost reduction and improved stock utilization within 12 months."
              stats={[
                { value: "£600k", label: "Projected Savings" },
                { value: "40%", label: "Faster Processing" },
                { value: "12", label: "Homes Freed" }
              ]}
            />
            <CaseStudyCard
              challenge="A Housing Association received 200+ succession enquiries annually, with 60% failing basic eligibility—wasting officer time on ineligible claims."
              solution="Deployed the Succession Fraud Filter to pre-qualify claims and generate evidence checklists before officer involvement."
              outcome="Officers now only process pre-qualified, evidence-ready applications."
              stats={[
                { value: "65%", label: "Fewer Invalid Claims" },
                { value: "8hrs", label: "Saved Per Week" },
                { value: "100%", label: "Evidence Complete" }
              ]}
            />
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            * Projected outcomes based on pilot methodology. Individual results may vary.
          </p>
        </div>
      </section>

      {/* Frontline Insights - Resource Hub */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="text-[#00703C]" size={20} />
                <span className="text-sm font-semibold text-[#00703C] uppercase tracking-wide">Frontline Insights</span>
              </div>
              <h2 className="text-2xl font-bold text-[#333333]">From a Decade on the Ground</h2>
            </div>
            <button className="text-[#00703C] font-medium text-sm flex items-center gap-1 hover:underline">
              View All Insights <ExternalLink size={14} />
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {insights.map((insight, index) => (
              <InsightCard key={index} {...insight} />
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 bg-[#333333] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">The Value to Your Authority</h2>
          <p className="text-gray-300 mb-12 max-w-2xl mx-auto">
            TenURes doesn't replace your officers—it arms them with pre-qualified, evidence-ready residents.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <p className="text-4xl font-bold text-[#00703C] mb-2">30-40%</p>
              <p className="text-gray-300">Reduction in Manual Processing</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#00703C] mb-2">£0</p>
              <p className="text-gray-300">Pilot Cost to Authority</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#00703C] mb-2">10yr</p>
              <p className="text-gray-300">Frontline Logic Engine</p>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="py-20 bg-white" id="roi-calculator">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 bg-[#00703C] bg-opacity-10 text-[#00703C] text-sm font-medium mb-4">
              ROI Calculator
            </span>
            <h2 className="text-3xl font-bold text-[#333333] mb-4">See Your Potential Savings</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Enter your housing stock details to see how much TenURes could save your authority through early intervention and reduced evictions.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Calculator Inputs */}
            <div className="bg-[#F8F8F8] border border-gray-200 p-6">
              <h3 className="font-bold text-[#333333] mb-6 flex items-center gap-2">
                <Calculator className="text-[#00703C]" size={20} />
                Your Authority Details
              </h3>
              
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-[#333333] mb-2">
                    Total Housing Stock *
                  </label>
                  <input
                    type="number"
                    className="govtech-input"
                    placeholder="e.g., 5000"
                    value={roiData.stockSize}
                    onChange={(e) => {
                      setRoiData({...roiData, stockSize: e.target.value});
                      setRoiCalculated(false);
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Number of social housing units you manage</p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-[#333333] mb-2">
                    Current Arrears Rate (%)
                  </label>
                  <select
                    className="govtech-input"
                    value={roiData.arrearsRate}
                    onChange={(e) => {
                      setRoiData({...roiData, arrearsRate: e.target.value});
                      setRoiCalculated(false);
                    }}
                  >
                    <option value="5">5% - Below average</option>
                    <option value="8">8% - Average</option>
                    <option value="12">12% - Above average</option>
                    <option value="15">15% - High</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-[#333333] mb-2">
                    Average Eviction Cost (£)
                  </label>
                  <select
                    className="govtech-input"
                    value={roiData.avgEvictionCost}
                    onChange={(e) => {
                      setRoiData({...roiData, avgEvictionCost: e.target.value});
                      setRoiCalculated(false);
                    }}
                  >
                    <option value="8000">£8,000 - Lower estimate</option>
                    <option value="12000">£12,000 - Average</option>
                    <option value="15000">£15,000 - Higher estimate</option>
                    <option value="20000">£20,000 - Including rehousing</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Includes legal fees, court costs, void period, and staff time</p>
                </div>
                
                <button
                  onClick={() => setRoiCalculated(true)}
                  disabled={!roiData.stockSize}
                  className="govtech-btn govtech-btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Calculate My Savings
                  <ArrowRight size={18} className="ml-2" />
                </button>
              </div>
            </div>
            
            {/* Results */}
            <div className={`border-2 transition-all ${roiCalculated ? 'bg-[#00703C] border-[#00703C] text-white' : 'bg-gray-100 border-gray-200'} p-6`}>
              {!roiCalculated ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mb-4">
                    <BarChart3 className="text-gray-400" size={32} />
                  </div>
                  <h3 className="font-bold text-gray-500 mb-2">Your Results Will Appear Here</h3>
                  <p className="text-sm text-gray-400">Enter your details and click calculate</p>
                </div>
              ) : (
                <div>
                  <h3 className="font-bold text-xl mb-6 flex items-center gap-2">
                    <CheckCircle size={24} />
                    Your Projected Annual Savings
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Main Saving */}
                    <div className="bg-white bg-opacity-10 p-4 rounded">
                      <p className="text-green-200 text-sm mb-1">Estimated Cost Savings</p>
                      <p className="text-4xl font-bold">£{roiResults.costSaved.toLocaleString()}</p>
                      <p className="text-green-200 text-sm">per year</p>
                    </div>
                    
                    {/* Breakdown */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white bg-opacity-10 p-3 rounded text-center">
                        <p className="text-3xl font-bold">{roiResults.evictionsPrevented}</p>
                        <p className="text-green-200 text-xs">Evictions Prevented</p>
                      </div>
                      <div className="bg-white bg-opacity-10 p-3 rounded text-center">
                        <p className="text-3xl font-bold">{roiResults.residentsHelped}</p>
                        <p className="text-green-200 text-xs">Residents Helped Early</p>
                      </div>
                      <div className="bg-white bg-opacity-10 p-3 rounded text-center">
                        <p className="text-3xl font-bold">{roiResults.officerHoursSaved}</p>
                        <p className="text-green-200 text-xs">Officer Hours Saved</p>
                      </div>
                      <div className="bg-white bg-opacity-10 p-3 rounded text-center">
                        <p className="text-3xl font-bold">{roiResults.atRiskResidents}</p>
                        <p className="text-green-200 text-xs">At-Risk Residents Identified</p>
                      </div>
                    </div>
                    
                    {/* Email Capture */}
                    {!roiEmailSent ? (
                      <div className="bg-white p-4 rounded">
                        <p className="text-[#333333] text-sm font-medium mb-2">Get this report emailed to you:</p>
                        <form onSubmit={handleRoiEmailSubmit} className="flex gap-2">
                          <input
                            type="email"
                            required
                            placeholder="your@email.gov.uk"
                            className="govtech-input flex-1 text-[#333333]"
                            value={roiEmail}
                            onChange={(e) => setRoiEmail(e.target.value)}
                          />
                          <button type="submit" className="govtech-btn govtech-btn-primary whitespace-nowrap">
                            <Send size={16} />
                          </button>
                        </form>
                      </div>
                    ) : (
                      <div className="bg-white p-4 rounded text-center">
                        <CheckCircle className="text-[#00703C] mx-auto mb-2" size={24} />
                        <p className="text-[#333333] text-sm font-medium">Report sent to {roiEmail}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Assumptions Note */}
                  <p className="text-xs text-green-200 mt-4">
                    * Based on 35% tool adoption rate and 78% intervention success rate from pilot data
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* CTA */}
          {roiCalculated && (
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">Ready to see these savings in action?</p>
              <button
                onClick={() => document.getElementById('pilot-form').scrollIntoView({ behavior: 'smooth' })}
                className="govtech-btn govtech-btn-primary"
              >
                Request a Free Pilot <ArrowRight size={18} className="ml-2" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Pilot Request Form */}
      <section id="pilot-form" className="py-20 bg-[#F8F8F8]">
        <div className="max-w-xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-[#333333] mb-4">Request a Zero-Cost Pilot</h2>
            <p className="text-gray-600">
              Let us demonstrate the value with your Authority's workflow. No commitment, no cost.
            </p>
          </div>

          {formSubmitted ? (
            <div className="text-center py-12 bg-[#F8F8F8] border border-gray-200">
              <div className="w-16 h-16 rounded-full bg-[#00703C] flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-[#333333] mb-2">Thank You</h3>
              <p className="text-gray-600">We'll be in touch within 2 working days.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-[#333333] mb-2">Your Name *</label>
                <input
                  type="text"
                  required
                  className="govtech-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#333333] mb-2">Work Email *</label>
                <input
                  type="email"
                  required
                  className="govtech-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#333333] mb-2">Local Authority / Housing Association *</label>
                <input
                  type="text"
                  required
                  className="govtech-input"
                  placeholder="e.g., London Borough of Hackney"
                  value={formData.authority}
                  onChange={(e) => setFormData({ ...formData, authority: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#333333] mb-2">Your Role *</label>
                <input
                  type="text"
                  required
                  className="govtech-input"
                  placeholder="e.g., Head of Housing, Lettings Manager"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#333333] mb-2">Primary Challenge (Optional)</label>
                <textarea
                  className="govtech-input min-h-[100px]"
                  placeholder="What's your biggest operational pain point?"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                />
              </div>
              
              <button type="submit" className="govtech-btn govtech-btn-primary w-full">
                Submit Pilot Request
                <ArrowRight className="ml-2" size={18} />
              </button>
              
              <p className="text-xs text-gray-500 text-center">
                By submitting, you agree to be contacted about the TenURes pilot program.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <span className="text-xl font-bold">
                Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
              </span>
              <p className="text-gray-500 text-sm mt-4">
                The Digital Triage Layer for Social Housing
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-[#333333] mb-4">Modules</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><button onClick={() => navigate('/succession-triage')} className="hover:text-[#00703C]">Succession Rights</button></li>
                <li><button onClick={() => navigate('/rtb-triage')} className="hover:text-[#00703C]">Right to Buy</button></li>
                <li><button onClick={() => navigate('/liveability-triage')} className="hover:text-[#00703C]">Downsizing & Exchange</button></li>
                <li><button onClick={() => navigate('/milestone-guardian')} className="hover:text-[#00703C]">Tenancy Milestones</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-[#333333] mb-4">For Residents</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><button onClick={() => navigate('/resident')} className="hover:text-[#00703C]">Resident Portal</button></li>
                <li><button onClick={() => navigate('/succession-triage')} className="hover:text-[#00703C]">Free Triage Tools</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-[#333333] mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><button onClick={() => navigate('/about')} className="hover:text-[#00703C]">About</button></li>
                <li><button onClick={() => navigate('/privacy')} className="hover:text-[#00703C]">Privacy</button></li>
                <li><button onClick={() => navigate('/contact')} className="hover:text-[#00703C]">Contact</button></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Ten<span className="text-[#00703C] font-semibold">UR</span>es.co.uk — Built with a Decade of Frontline Housing Expertise
            </p>
            <div className="flex items-center gap-6 text-xs text-gray-400">
              <span>Housing Act 1985 Compliant</span>
              <span>GDPR Compliant</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
